"use client";

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  getProjectBySlug,
  getProjectTokens,
  getPublicAnnouncements,
  getProjectRichlist,
  ProjectWithDetails,
  TokenMarketData,
  ProjectAnnouncement,
  ProjectRichlistResult,
} from '../api/projects';
import { ProjectRichlistTable } from '../components/project/ProjectRichlistTable';
import { RewardsTab } from '../components/rewards/RewardsTab';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../ui/components/Button';
import { Tabs } from '../ui/components/Tabs';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { formatPrice, formatFloorPrice } from '../utils/formatters';
import { renderContentWithLinks } from '../utils/linkify';
import {
  FeatherExternalLink,
  FeatherTwitter,
  FeatherMessageCircle,
  FeatherGlobe,
  FeatherTrendingUp,
  FeatherTrendingDown,
  FeatherArrowLeft,
  FeatherBell,
  FeatherSettings,
} from '@subframe/core';

// Simple relative time formatter
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// DEX URLs for trading
const DEX_LINKS = {
  firstLedger: (issuer: string, currency: string) =>
    `https://firstledger.net/token/${issuer}/${currency}`,
  magnetic: (issuer: string, currency: string) =>
    `https://xmagnetic.org/tokens/${currency}+${issuer}?network=mainnet`,
  sologenic: (issuer: string, currency: string) =>
    `https://sologenic.org/trade?market=${encodeURIComponent(currency)}%2B${encodeURIComponent(issuer)}%2FXRP`,
};

// Decode hex currency code to readable string
function decodeCurrencyCode(code: string): string {
  // If it's a standard 3-char code, return as-is
  if (code.length <= 3) return code;

  // If it's a 40-char hex code, decode it
  if (code.length === 40 && /^[A-Fa-f0-9]+$/.test(code)) {
    try {
      // Remove trailing zeros and convert hex to ASCII
      const trimmed = code.replace(/0+$/, '');
      let result = '';
      for (let i = 0; i < trimmed.length; i += 2) {
        const charCode = parseInt(trimmed.substring(i, i + 2), 16);
        if (charCode > 0 && charCode < 128) {
          result += String.fromCharCode(charCode);
        }
      }
      return result || code;
    } catch {
      return code;
    }
  }

  return code;
}

// Reusable stat cell component for token metrics
function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center justify-center p-4 text-center">
      <span className="text-caption font-caption text-subtext-color mb-1">{label}</span>
      <span className="text-body-bold font-body-bold text-default-font">{value}</span>
    </div>
  );
}

type ProjectTab = 'info' | 'announcements' | 'richlist' | 'rewards';

export const ProjectDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectWithDetails | null>(null);
  const [tokens, setTokens] = useState<TokenMarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Announcement state
  const [publicAnnouncements, setPublicAnnouncements] = useState<ProjectAnnouncement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);

  // Richlist state
  const [richlist, setRichlist] = useState<ProjectRichlistResult | null>(null);
  const [richlistLoading, setRichlistLoading] = useState(false);

  // Check if project richlist feature is enabled
  const isRichlistEnabled = useMemo(() => {
    return project?.enabled_features?.includes('project_richlist') ?? false;
  }, [project?.enabled_features]);

  // Check if project rewards feature is enabled
  const isRewardsEnabled = useMemo(() => {
    return project?.enabled_features?.includes('project_rewards') ?? false;
  }, [project?.enabled_features]);

  // Tab state - default to 'info' or read from URL
  const rawTab = searchParams.get('tab');
  const initActiveTab: ProjectTab = (rawTab as ProjectTab) || 'info';
  const [activeTab, setActiveTab] = useState<ProjectTab>(initActiveTab);

  // Handle tab change
  const handleTabChange = (newTab: ProjectTab) => {
    setActiveTab(newTab);

    // Update URL with new tab
    const params = new URLSearchParams(searchParams);
    if (newTab === 'info') {
      params.delete('tab');
    } else {
      params.set('tab', newTab);
    }
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (!slug) return;

    const fetchProject = async () => {
      setLoading(true);
      setError(null);

      try {
        const projectData = await getProjectBySlug(slug);
        setProject(projectData);

        // Fetch live token data
        if (projectData.tokens && projectData.tokens.length > 0) {
          try {
            const tokenData = await getProjectTokens(slug);
            setTokens(tokenData);
          } catch (err) {
            console.error('Failed to fetch token data:', err);
            // Use cached token data from project
            setTokens(projectData.tokens || []);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();

    // Refresh token data every 60 seconds
    const interval = setInterval(async () => {
      if (slug) {
        try {
          const tokenData = await getProjectTokens(slug);
          setTokens(tokenData);
        } catch (err) {
          console.error('Failed to refresh token data:', err);
        }
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [slug]);

  // Load announcements when tab changes
  useEffect(() => {
    if (!slug || !project || activeTab !== 'announcements') return;

    const loadAnnouncements = async () => {
      setAnnouncementsLoading(true);

      try {
        const result = await getPublicAnnouncements(slug, 25);
        setPublicAnnouncements(result.announcements);
      } catch (err) {
        console.error('Failed to load announcements:', err);
      } finally {
        setAnnouncementsLoading(false);
      }
    };

    loadAnnouncements();
  }, [slug, project, activeTab]);

  // Load richlist when tab changes and feature is enabled
  useEffect(() => {
    if (!slug || !project || !isRichlistEnabled) return;
    if (activeTab !== 'richlist') return;
    if (richlist) return; // Already loaded

    const loadRichlist = async () => {
      setRichlistLoading(true);

      try {
        const result = await getProjectRichlist(slug);
        setRichlist(result);
      } catch (err) {
        console.error('Failed to load richlist:', err);
      } finally {
        setRichlistLoading(false);
      }
    };

    loadRichlist();
  }, [slug, project, activeTab, isRichlistEnabled, richlist]);

  // Format price change with color and icon
  const formatPriceChange = (change: number | null | undefined) => {
    if (change === null || change === undefined) return null;
    const isPositive = change >= 0;
    const Icon = isPositive ? FeatherTrendingUp : FeatherTrendingDown;
    const colorClass = isPositive ? 'text-success-600' : 'text-error-600';
    return (
      <span className={`flex items-center gap-1 ${colorClass}`}>
        <Icon className="w-4 h-4" />
        {isPositive ? '+' : ''}{change.toFixed(2)}%
      </span>
    );
  };

  // Format large numbers
  const formatLargeNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '--';
    if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
    // For small numbers, show as integer if whole number, otherwise 2 decimals
    if (Number.isInteger(num) || Math.abs(num - Math.round(num)) < 0.001) {
      return Math.round(num).toLocaleString();
    }
    return num.toFixed(2);
  };

  // Format USD values with $ prefix and K/M suffix
  const formatUsd = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '--';
    if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(2)}B`;
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `$${(num / 1_000).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full items-center justify-center py-32">
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="lg" />
            <span className="text-body font-body text-subtext-color">Loading project...</span>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (error || !project) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full items-center justify-center py-32">
          <div className="flex flex-col items-center gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">Project Not Found</span>
            <span className="text-body font-body text-subtext-color">{error || 'This project does not exist'}</span>
            <Link to="/projects">
              <Button variant="brand-secondary" icon={<FeatherArrowLeft />}>
                Back to Projects
              </Button>
            </Link>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="container max-w-none flex h-full w-full flex-col items-start gap-8 bg-default-background py-8 overflow-auto mobile:gap-4 mobile:py-4">
        {/* Back Link */}
        <Link to="/projects" className="flex items-center gap-2 text-subtext-color hover:text-default-font transition-colors">
          <FeatherArrowLeft className="w-4 h-4" />
          <span className="text-caption font-caption">Back to Projects</span>
        </Link>

        {/* Project Header */}
        <div className="flex w-full flex-col gap-6">
          {/* Banner */}
          {project.banner_url && (
            <div className="w-full max-w-4xl aspect-[3/1] rounded-lg overflow-hidden bg-neutral-100">
              <img
                src={project.banner_url}
                alt={`${project.name} banner`}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Project Info */}
          <div className="flex gap-6 mobile:flex-col">
            {/* Logo */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-lg overflow-hidden bg-neutral-100 border border-neutral-border">
                {project.image_url ? (
                  <img
                    src={project.image_url}
                    alt={project.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-heading-1 font-heading-1 text-neutral-400">
                      {project.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Name and Description */}
            <div className="flex-1 flex flex-col gap-3">
              <h1 className="text-heading-1 font-heading-1 text-default-font mobile:text-heading-2">
                {project.name}
              </h1>

              {project.description && (
                <p className="text-body font-body text-subtext-color">
                  {project.description}
                </p>
              )}

              {/* Tags */}
              {project.tags && project.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="px-3 py-1 rounded-full bg-neutral-100 text-caption font-caption text-subtext-color"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Social Links & Manage Button */}
              <div className="flex items-center gap-3 flex-wrap">
                {project.website_url && (
                  <a
                    href={project.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-caption font-caption text-brand-600 hover:text-brand-700 transition-colors"
                  >
                    <FeatherGlobe className="w-4 h-4" />
                    Website
                  </a>
                )}
                {project.twitter_url && (
                  <a
                    href={project.twitter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-caption font-caption text-brand-600 hover:text-brand-700 transition-colors"
                  >
                    <FeatherTwitter className="w-4 h-4" />
                    Twitter
                  </a>
                )}
                {project.discord_url && (
                  <a
                    href={project.discord_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-caption font-caption text-brand-600 hover:text-brand-700 transition-colors"
                  >
                    <FeatherMessageCircle className="w-4 h-4" />
                    Discord
                  </a>
                )}
                {project.isManager && (
                  <Button
                    variant="brand-secondary"
                    size="small"
                    icon={<FeatherSettings />}
                    onClick={() => navigate(`/creator/projects/${slug}`)}
                  >
                    Manage Project
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Tabs Section */}
        <div className="flex w-full flex-col items-start gap-6">
          {/* Tabs */}
          <div className="w-full overflow-x-auto scrollbar-hide">
            <Tabs className="min-w-max">
              <Tabs.Item
                active={activeTab === 'info'}
                onClick={() => handleTabChange('info')}
              >
                Info
              </Tabs.Item>
              <Tabs.Item
                active={activeTab === 'announcements'}
                onClick={() => handleTabChange('announcements')}
              >
                Announcements
              </Tabs.Item>
              {isRichlistEnabled && (
                <Tabs.Item
                  active={activeTab === 'richlist'}
                  onClick={() => handleTabChange('richlist')}
                >
                  Richlist
                </Tabs.Item>
              )}
              {isRewardsEnabled && (
                <Tabs.Item
                  active={activeTab === 'rewards'}
                  onClick={() => handleTabChange('rewards')}
                >
                  Rewards
                </Tabs.Item>
              )}
            </Tabs>
          </div>

          {/* Info Tab Content */}
          {activeTab === 'info' && (
            <div className="flex w-full flex-col items-start gap-8">
              {/* Token Section */}
              {tokens.length > 0 && (
                <div className="w-full flex flex-col gap-4">
                  <h2 className="text-heading-2 font-heading-2 text-default-font">
                    {tokens.length === 1 ? 'Token' : 'Tokens'}
                  </h2>

                  {tokens.map((token) => (
                    <div
                      key={token.id}
                      className="w-full rounded-lg border border-neutral-border bg-default-background overflow-hidden"
                    >
                      {/* Token Header - Name, Price, and Trade Links */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-neutral-50 border-b border-neutral-border">
                        <div className="flex items-center gap-4">
                          {/* Token Icon/Badge */}
                          <div className="w-12 h-12 rounded-full bg-brand-600 flex items-center justify-center text-white text-heading-3 font-heading-3 flex-shrink-0">
                            {(token.display_name || 'T').charAt(0)}
                          </div>
                          <div>
                            <h3 className="text-heading-3 font-heading-3 text-default-font">
                              ${token.display_name || decodeCurrencyCode(token.currency_code)}
                            </h3>
                            {token.has_liquidity && token.price_xrp !== null && (
                              <div className="flex items-center gap-2">
                                <span className="text-body font-body text-default-font">
                                  {formatPrice(token.price_xrp)}
                                </span>
                                {formatPriceChange(token.price_change_24h)}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Trade Links - Inline */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-caption font-caption text-subtext-color mr-1">Trade on:</span>
                          <a
                            href={DEX_LINKS.firstLedger(token.issuer_address, token.currency_code)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-neutral-100 hover:bg-neutral-200 text-caption font-caption text-default-font transition-colors"
                          >
                            FirstLedger
                            <FeatherExternalLink className="w-3 h-3" />
                          </a>
                          <a
                            href={DEX_LINKS.magnetic(token.issuer_address, token.currency_code)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-neutral-100 hover:bg-neutral-200 text-caption font-caption text-default-font transition-colors"
                          >
                            xMagnetic
                            <FeatherExternalLink className="w-3 h-3" />
                          </a>
                          <a
                            href={DEX_LINKS.sologenic(token.issuer_address, token.currency_code)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-neutral-100 hover:bg-neutral-200 text-caption font-caption text-default-font transition-colors"
                          >
                            Sologenic
                            <FeatherExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>

                      {/* Token Stats Grid */}
                      {token.has_liquidity ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y sm:divide-y-0 divide-neutral-border">
                          <StatCell
                            label="Market Cap"
                            value={token.market_cap_usd != null ? formatUsd(token.market_cap_usd) : '--'}
                          />
                          <StatCell label="Supply" value={token.total_supply !== null ? formatLargeNumber(token.total_supply) : '--'} />
                          <StatCell label="Holders" value={token.holder_count != null ? formatLargeNumber(token.holder_count) : '--'} />
                          <StatCell label="Trustlines" value={token.trustline_count != null ? formatLargeNumber(token.trustline_count) : '--'} />
                          <StatCell label="24h Volume" value={token.volume_24h_xrp != null ? `${formatLargeNumber(token.volume_24h_xrp)} XRP` : '--'} />
                          <StatCell
                            label="Liquidity"
                            value={
                              token.amm_exists && token.amm_liquidity_usd != null
                                ? formatUsd(token.amm_liquidity_usd)
                                : token.amm_exists === false
                                ? 'None'
                                : '--'
                            }
                          />
                        </div>
                      ) : (
                        <div className="p-6 text-center text-body font-body text-subtext-color">
                          No liquidity available for this token
                        </div>
                      )}

                      {/* Last Updated */}
                      {token.last_updated && (
                        <div className="px-4 py-2 text-caption font-caption text-subtext-color border-t border-neutral-border bg-neutral-50">
                          Updated {new Date(token.last_updated).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Collections Section */}
              {project.collections && project.collections.length > 0 && (
                <div className="w-full flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-heading-2 font-heading-2 text-default-font">Collections</h2>
                    <span className="text-caption font-caption text-subtext-color">
                      {project.collections.length} collection{project.collections.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {/* Collections are displayed in custom order set by admin */}
                    {project.collections.map((collection) => (
                      <Link
                        key={collection.id}
                        to={`/collections/${collection.slug || collection.id}`}
                        className="group flex flex-col rounded-lg border border-solid border-neutral-border bg-default-background overflow-hidden hover:border-brand-500 hover:shadow-lg transition-all"
                      >
                        {/* Collection Image */}
                        <div className="relative aspect-square bg-neutral-100 overflow-hidden">
                          {collection.image_url ? (
                            <img
                              src={collection.image_url}
                              alt={collection.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="text-heading-2 font-heading-2 text-neutral-400">
                                {collection.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Collection Info */}
                        <div className="flex flex-col gap-2 p-4">
                          <h3 className="text-body-bold font-body-bold text-default-font line-clamp-1">
                            {collection.name}
                          </h3>

                          <div className="flex items-center justify-between text-caption font-caption">
                            <div className="flex items-center gap-1">
                              <span className="text-subtext-color">Floor:</span>
                              <span className="text-default-font">
                                {collection.floor_price !== null
                                  ? formatFloorPrice(collection.floor_price)
                                  : '--'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-subtext-color">Supply:</span>
                              <span className="text-default-font">{collection.total_supply}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* No Collections Message */}
              {(!project.collections || project.collections.length === 0) && (
                <div className="w-full flex flex-col gap-4">
                  <h2 className="text-heading-2 font-heading-2 text-default-font">Collections</h2>
                  <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
                    <span className="text-body font-body text-subtext-color">
                      No collections associated with this project yet
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Announcements Tab Content */}
          {activeTab === 'announcements' && (
            <div className="flex w-full flex-col items-start gap-6">
              <h2 className="text-heading-2 font-heading-2 text-default-font">Announcements</h2>

              {announcementsLoading ? (
                <div className="flex w-full items-center justify-center py-12">
                  <LoadingSpinner size="md" />
                </div>
              ) : publicAnnouncements.length === 0 ? (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
                  <div className="flex flex-col items-center gap-2">
                    <FeatherBell className="w-8 h-8 text-neutral-400" />
                    <span className="text-body font-body text-subtext-color">
                      No announcements yet
                    </span>
                  </div>
                </div>
              ) : (
                <div className="w-full flex flex-col gap-4">
                  {publicAnnouncements.map((announcement) => (
                    <div key={announcement.id} className="w-full">
                      <hr className="border-neutral-border mb-4" />
                      <p className="text-body font-body text-default-font whitespace-pre-wrap">
                        {renderContentWithLinks(announcement.content)}
                      </p>
                      <span className="text-caption font-caption text-subtext-color mt-1 block">
                        {formatRelativeTime(announcement.created_at)}
                      </span>
                    </div>
                  ))}
                  <hr className="border-neutral-border" />
                </div>
              )}
            </div>
          )}

          {/* Richlist Tab Content */}
          {activeTab === 'richlist' && isRichlistEnabled && (
            <div className="flex w-full flex-col items-start gap-6">
              <h2 className="text-heading-2 font-heading-2 text-default-font">Project Richlist</h2>

              {richlistLoading ? (
                <div className="flex w-full items-center justify-center py-12">
                  <LoadingSpinner size="md" />
                </div>
              ) : richlist ? (
                <div className="w-full">
                  <ProjectRichlistTable richlist={richlist} projectName={project.name} />
                </div>
              ) : (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
                  <span className="text-body font-body text-subtext-color">
                    Failed to load richlist data
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Rewards Tab Content (Public) */}
          {activeTab === 'rewards' && isRewardsEnabled && slug && (
            <RewardsTab projectSlug={slug} />
          )}

        </div>
      </div>
    </DefaultPageLayout>
  );
};
